import { API, deleteRequest, getRequest, postRequest, putRequest } from '@/api/api.ts';
import { isPermissionDenied } from '@/common/error.ts';
import { toast } from '@/utils/toast.ts';

import type { Transaction, TransactionCategory } from './transactions.dto.ts';

export const getTransactionsCategories = async () => {
  try {
    const response = await getRequest<TransactionCategory[]>(API.EXPENSES, '/transaction/category');
    return response.data || [];
  } catch (error) {
    if (isPermissionDenied(error)) return []; // no access — expected, not an error
    const message =
      error instanceof Error ? error.message : "Couldn't load the categories. Try again";
    toast.error('Couldn’t load the categories. Please try again.');
    throw new Error(message);
  }
};

export const createTransactionCategory = async (title: string, color: string, icon?: string) => {
  try {
    const response = await postRequest<
      TransactionCategory,
      { title: string; color: string; icon?: string }
    >(API.EXPENSES, '/transaction/category', {
      title,
      color,
      icon,
    });
    return response.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Couldn't create the category";
    toast.error('Couldn’t create the category. Please try again.');
    throw new Error(message);
  }
};

export const updateTransactionCategory = async (
  categoryId: string | undefined,
  title: string,
  color: string,
  icon?: string,
) => {
  try {
    const response = await putRequest<
      TransactionCategory,
      { title: string; color: string; icon?: string }
    >(API.EXPENSES, `/transaction/category/${categoryId}`, {
      title,
      color,
      icon,
    });
    return response.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Couldn't update the category";
    toast.error('Couldn’t update the category. Please try again.');
    throw new Error(message);
  }
};

/**
 * Deletes a category and hands its transactions and budgets over to `targetCategoryId`.
 * Omitting the target lets wallet fall back to the vessel's protected "Other" category.
 * A year both categories plan for keeps the sum of the two plans, not the target's alone.
 */
export const deleteTransactionCategory = async (categoryId: string, targetCategoryId?: string) => {
  try {
    const response = await deleteRequest(
      API.EXPENSES,
      `/transaction/category/${categoryId}`,
      undefined,
      false,
      targetCategoryId ? { target_category_id: targetCategoryId } : undefined,
    );
    return response.data;
  } catch (error) {
    const message = error instanceof Error ? error.message : "Couldn't delete the category";
    toast.error('Couldn’t delete the category. Please try again.');
    throw new Error(message);
  }
};

/**
 * How many transactions are filed under a category. Asked with `limit=1` because only the
 * total matters: the page's `count` is the full match count, not the rows returned. Unlike
 * the lists on screen this asks over every date, so an empty answer really means empty.
 */
export const countTransactionsByCategory = async (categoryId: string): Promise<number> => {
  const response = await getRequest<{ items: Transaction[]; count: number }>(
    API.EXPENSES,
    `/transaction?limit=1&page=1&expenses_category=${encodeURIComponent(categoryId)}`,
  );
  return response.data.count ?? 0;
};
